import React, { useCallback } from 'react';
import { ChipSelector } from './ChipSelector';
import DayFirstDate from "./DayFirstDate";

// ── Rating options ─────────────────────────────────────────────────
const RATING_OPTIONS = [
  { value: '0', label: '0 — Never' },
  { value: '1', label: '1 — Rarely' },
  { value: '2', label: '2 — Sometimes' },
  { value: '3', label: '3 — Often' },
  { value: '4', label: '4 — Don\'t Know' },
];

// ── Styles ─────────────────────────────────────────────────────────
const sectionStyle = { marginBottom: 32, paddingBottom: 16, borderBottom: '1px solid #e5e7eb' };
const sectionTitle = { fontSize: '1.25rem', fontWeight: 700, color: '#1e3a5f', marginBottom: 12 };
const fieldGap = { marginBottom: 16 };
const labelStyle = { fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' };
const inputStyle = {
  width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db',
  fontSize: '0.875rem', fontFamily: 'inherit', color: '#1a202c',
};
const textareaStyle = { ...inputStyle, minHeight: 80, resize: 'vertical' };
const itemCard = {
  padding: 14, borderRadius: 10, border: '1px solid #e5e7eb', backgroundColor: '#f9fafb', marginBottom: 12,
};
const itemQuestion = { fontSize: '0.8125rem', color: '#374151', marginBottom: 8, lineHeight: 1.5 };

// ── Question stems (shared) ────────────────────────────────────────
const QUESTIONS = [
  'fail to give close attention to details or make careless mistakes',
  'fail to sustain attention in tasks or activities',
  'not listen to what was being said to them',
  'fail to follow through on instructions or finish tasks',
  'struggle to organise tasks and activities',
  'avoid or strongly dislike tasks requiring sustained mental effort',
  'lose things necessary for tasks and activities',
  'become easily distracted by external stimuli or thoughts',
  'become forgetful in daily activities',
  'fidget with hands or feet, or squirm in seat',
  'leave their seat when remaining seated was expected',
  'run about or climb excessively, or feel internally restless',
  'have difficulty engaging in leisure activities quietly',
  'feel "on the go" or act as if "driven by a motor"',
  'talk excessively',
  'blurt out answers before questions had been completed',
  'have difficulty waiting their turn',
  'interrupt or intrude on others',
];

// ── Component ──────────────────────────────────────────────────────
export default function ACEplusInformantForm({ responses, setResponses, variant }) {
  const r = responses;
  const set = useCallback((key, val) => setResponses(prev => ({ ...prev, [key]: val })), [setResponses]);

  const isChildhood = variant === 'childhood';
  const timeTag = isChildhood
    ? { bg: '#dbeafe', color: '#1e40af', text: 'CHILDHOOD' }
    : { bg: '#fef3c7', color: '#92400e', text: 'RECENT' };

  // Number of questions varies: childhood=18+3text, adult=18
  const questionCount = QUESTIONS.length;

  return (
    <div style={{ maxWidth: 800 }}>

      {/* ─── Demographics (childhood version has 3 text fields) ─── */}
      {isChildhood && (
        <div style={sectionStyle}>
          <h2 style={sectionTitle}>About the Person</h2>
          <div style={fieldGap}>
            <label style={labelStyle}>Name of person you are completing this about</label>
            <input type="text" style={inputStyle} value={r.subject_name || ''} onChange={e => set('subject_name', e.target.value)} data-testid="aceplus-subject" />
          </div>
          <div style={{ display: 'flex', gap: 16, ...fieldGap }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Date of birth</label>
              <DayFirstDate label="Date of birth" notFuture inputStyle={inputStyle} value={r.subject_dob || ''} onChange={v => set('subject_dob', v)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Your relationship to them</label>
              <input type="text" style={inputStyle} value={r.relationship || ''} onChange={e => set('relationship', e.target.value)} placeholder="e.g. Parent, Partner, Sibling" />
            </div>
          </div>
        </div>
      )}

      {/* ─── Rating scale guide ─── */}
      <div style={{ ...sectionStyle, backgroundColor: '#f0f7ff', padding: 16, borderRadius: 10, border: '1px solid #bfdbfe' }}>
        <p style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e3a5f', marginBottom: 8 }}>Rating Scale</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: '0.8125rem', color: '#374151' }}>
          <span><strong>0</strong> — Never</span>
          <span><strong>1</strong> — Rarely</span>
          <span><strong>2</strong> — Sometimes</span>
          <span><strong>3</strong> — Often</span>
          <span><strong>4</strong> — Don't Know</span>
        </div>
        <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginTop: 8 }}>
          All questions refer to{' '}
          <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 700, backgroundColor: timeTag.bg, color: timeTag.color }}>
            {timeTag.text}
          </span>{' '}
          behaviour.
        </p>
      </div>

      {/* ─── Questions ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>ADHD Symptom Questions</h2>
        {(() => {
          const firstName = (r.subject_name || '').split(' ')[0].trim();
          const leadIn = isChildhood
            ? (firstName ? `Did ${firstName}...` : 'Did this person...')
            : (firstName ? `Does ${firstName}...` : 'Does this person...');
          return (
            <>
              <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginBottom: 8 }}>
                {isChildhood ? 'Rate how often this applied as a child.' : 'Rate how often this applies currently.'}
              </p>
              <p style={{ fontSize: '1rem', fontWeight: 600, color: '#1e3a5f', marginBottom: 12 }}>{leadIn}</p>
            </>
          );
        })()}
        {QUESTIONS.map((q, idx) => (
          <div key={idx} style={itemCard}>
            <p style={itemQuestion}>...{q}</p>
            <ChipSelector options={RATING_OPTIONS} value={r[`item_${idx + 1}`] || ''} onChange={v => set(`item_${idx + 1}`, v)} />
          </div>
        ))}
      </div>

      {/* ─── Summary (childhood only) ─── */}
      {isChildhood && (
        <div style={{ marginBottom: 16 }}>
          <h2 style={sectionTitle}>Summary</h2>
          <div style={fieldGap}>
            <label style={labelStyle}>At what age did you first notice these symptoms?</label>
            <input type="text" style={inputStyle} value={r.onset_age || ''} onChange={e => set('onset_age', e.target.value)} />
          </div>
          <div style={fieldGap}>
            <label style={labelStyle}>Have these symptoms been present over the past six months?</label>
            <textarea style={textareaStyle} value={r.duration_impact || ''} onChange={e => set('duration_impact', e.target.value)} />
          </div>
          <div style={fieldGap}>
            <label style={labelStyle}>How have these symptoms impacted on functioning?</label>
            <textarea style={{ ...textareaStyle, minHeight: 100 }} value={r.functional_impact || ''} onChange={e => set('functional_impact', e.target.value)} />
          </div>
        </div>
      )}
    </div>
  );
}
